define([
	"dojo/_base/declare",
	"csi/comm/PostRequest",
	"csi/constants",
	"csi/sessionConstants",
	"csi/xml",
	"csi/xml/Query"
], function(declare, PostRequest, constants, sessionConstants, xml, Query) {

	const {
		DEP_PATH,
		FMT_COMPLETE,
		FMT_COMPLETE_MAX,
		MODULE,
		MODULE_SEARCH,
		OPERATION,
		OPERATION_COUNT_XML,
		OPERATION_QUERY_URL,
		OPERATION_QUERY_XML,
		QRY_LIMIT_BEGIN,
		QRY_LIMIT_END,
		QRY_LIMIT_LIST,
		QRY_LIMIT_PATH,
		QRY_URL,
		QRY_XML,
		QUERY_RESULT_LIMIT,
		REQUEST_URL_OMIT_PARAMS,
	} = constants;

	return declare(null, {
		/**
		 * @property {boolean} customOperationSupportsPaging - indicates if request supports Qry.Limit.Begin/End and Fmt.Complete functionality 
		 * to return first page of nodes, but all urls (QueryByXML will be detected automatically)
		 */
		customOperationSupportsPaging: false,

		/**
		 * @property {string} customQueryByUrlOperation - operation to use to query nodes from URLs for request that supports paging
		 */
		customQueryByUrlOperation: null,

		// Query: query node for filtering
		_query: null,

		// Query: query for sorting
		_sortQuery: null,

		// PostRequest: request to issue to server
		_request: null,

		constructor(/*Object?*/params) {
			// summary: create request for search
			if (params) {
				this._request = new PostRequest(params.Module || MODULE_SEARCH, params.Operation || OPERATION_QUERY_XML, params);
			} else {
				this._request = new PostRequest(MODULE_SEARCH, OPERATION_QUERY_XML);
			}

			this._query = null;
			this._sortQuery = null;
		},

		toString() {
			return this._getRequestQuery().toString();
		},

		/**
		 * Add parameters to request
		 * @param {Record<string, any>} params 
		 * @return {Search}
		 */
		addParameters(params) {
			return this._request.mixinParameters(params);
		},

		hasParameter(/*String*/key) {
			return this._request.hasParameter(key);
		},

		getMaxResultSize() {
			return this.getParameter(FMT_COMPLETE_MAX);
		},

		getParameter(/*String*/key) {
			return this._request.getParameter(key);
		},

		getParameters() {
			return this._request.content;
		},

		addParameter(/*String*/key, /*Any*/value, /*Boolean*/bAllowDuplicate) {
			this._request.addParameter(key, value, bAllowDuplicate);
			return this;
		},

		removeParameter(/*String*/key) {
			this._request.removeParameter(key);
			return this;
		},

		addDependencyPaths(/*String[]*/paths) {
			this._request.addDependencyPaths(paths);
			return this;
		},

		/**
		 * Return dependency paths configured for search
		 * @return {string[]}
		 */
		getDependencyPaths() {
			return this._request.getParameter(DEP_PATH);
		},

		/**
		 * Return request to query for individual urls with basically same request parameters
		 * @param {cnl[]} urls 
		 * @param {string[]} [dependencyPaths] - override previous paths with new paths
		 * @return {PostRequest | null}
		 */
		getNodesRequest(urls, dependencyPaths) {
			if (this.supportsPaging() && urls.length) {
				const params = {
					...this._request.content,
					[QRY_URL]: urls,
				};

				if (dependencyPaths) {
					params[DEP_PATH] = dependencyPaths;
				}

				for (const param of REQUEST_URL_OMIT_PARAMS) {
					delete params[param];
				}

				return new PostRequest(MODULE_SEARCH, this.customQueryByUrlOperation || OPERATION_QUERY_URL, params);
			}

			return null;
		},

		/**
		 * Return true if request is standard QueryByXML search
		 * @return {boolean}
		 */
		isQueryByXML() {
			const {_request} = this;
			const module = _request.getParameter(MODULE);
			const operation = _request.getParameter(OPERATION);
			return (module === MODULE_SEARCH) && (operation === OPERATION_QUERY_XML);
		},

		/**
		 * Set limits on which nodes will be returned
		 * @param {number} nBegin 
		 * @param {number} nEnd 
		 * @param {boolean} [includeRefs] - indicates if all urls should be returned in addition to nodes on page
		 * @return {Search}
		 */
		setPageLimits(nBegin, nEnd, includeRefs) {
			if (nBegin > 0) {
				this.addParameter(QRY_LIMIT_BEGIN, nBegin);
			}

			if (nEnd > 0) {
				this.addParameter(QRY_LIMIT_END, nEnd);
			}

			if (includeRefs) {
				this._request.addParameterIfMissing(FMT_COMPLETE, "Ref").addParameterIfMissing(FMT_COMPLETE_MAX, sessionConstants.maxResultSize);
			}

			return this;
		},

		setLimitAttribute(urlNode, idAttr) {
			this.setLimitPath(urlNode, xml.path(idAttr));
			return this;
		},

		setLimitPath(urlNode, path) {
			// allow multiple paths
			this.addParameter(QRY_LIMIT_PATH, xml.getUrlPath(urlNode, path), true);
			return this;
		},

		/** allow multiple urls */
		setLimitUrl(urlNode) {
			this.addParameter(QRY_LIMIT_LIST, urlNode, true);
			return this;
		},

		/** set sort query */
		setSortQuery(q) {
			this._sortQuery = q;
		},

		/**
		 * Return true if request supports paging parameters
		 * @return {boolean}
		 */
		supportsPaging() {
			return this.customOperationSupportsPaging || this.isQueryByXML();
		},

		byType(typeNode) {
			// summary: add condition to search by type
			this.getQuery().Node(xml.PROP_TYPE, xml.OP_EQUALS, typeNode);
			return this;
		},

		getQuery(/*Boolean*/bReset) {
			// summary: create query if not created already
			if (bReset || !this._query) {
				this._query = new Query();
			}

			return this._query;
		},

		getSortQuery(/*Boolean*/bReset) {
			// summary: create query for sorting if not created already
			if (bReset || !this._sortQuery) {
				this._sortQuery = new Query();
			}

			return this._sortQuery;
		},

		getRequest() {
			// summary: add query to request and return
			this._request.setBinaryQuery(QRY_XML, this._getRequestQuery());
			return this._request;
		},

		execute(ioArgs) {
			return this.getRequest().post(ioArgs);
		},

		/**
		 * Change operation to just count nodes rather than return them
		 */
		useCountOperation() {
			this.addParameter(OPERATION, OPERATION_COUNT_XML);
			return this;
		},

		_getRequestQuery() {
			// summary: return full query used in request
			var q;

			if (this._query && this._sortQuery) {
				q = new Query();
				q.importQuery(this._query);
				q.importQuery(this._sortQuery);
			} else {
				q = this._query || this._sortQuery || new Query();
			}

			return q;
		}
	});
});